import React, { FC, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Room } from '../../ts/interfaces/OphirResponse';
import { formatPriceCurrency } from '@bepro-travel/fe.utils';
import { Button, SelectCustom } from '@bepro-travel/fe.ui';
import { roomsCountSelect } from '../../constants';
import { useTranslation } from 'react-i18next';

interface OphirRoomsCardProps {
  roomsInfo: Room[];
  soldOut: boolean;
  searchToken: string;
}

interface RoomForCount {
  index: number;
  count: number;
}

const OphirRoomsCard: FC<OphirRoomsCardProps> = ({ roomsInfo, ...props }) => {
  const navigate = useNavigate();
  const [t] = useTranslation() as unknown as [(key: string) => string];
  const [total, setTotal] = useState(0);
  const [urlParams] = useSearchParams();
  const [roomsForCount, setRoomsForCount] = useState<RoomForCount[]>(
    roomsInfo[0].rooms.map((_, index) => ({ index, count: 0 }))
  );
  const onPressContinue = () => {
    const choosenRooms = roomsForCount.filter((room) => room.count);
    const roomsStr = roomsInfo[0].rooms.reduce((total, room, index) => {
      const choosenRoom = choosenRooms.find((choosenRoom) => choosenRoom.index === index);
      if (choosenRoom) {
        return `${total}${room.roomCode}-${room.roomType}-${choosenRoom.count},`;
      } else {
        return total;
      }
    }, '');

    urlParams.set('rooms', roomsStr.slice(0, -1));
    urlParams.set('searchToken', props.searchToken);
    urlParams.delete('room');

    const lang = localStorage.getItem('language') || 'he';
    const pathname = lang === 'he' ? '/tour/book' : '/tourru/book';

    navigate({ pathname, search: urlParams.toString() });
  };

  const setTotalPrice = (newCount: number, roomIndex: number) => {
    const newRoomsForCount = [
      ...roomsForCount.slice(0, roomIndex),
      { index: roomIndex, count: newCount },
      ...roomsForCount.slice(roomIndex + 1),
    ];
    setRoomsForCount(newRoomsForCount);
    const newTotal = newRoomsForCount.reduce((total, room) => {
      return total + roomsInfo[0].rooms[room.index].priceTotal * room.count;
    }, 0);
    setTotal(newTotal);
  };

  const areRooms = () => {
    return roomsForCount.some((room) => room.count != 0); // eslint-disable-line eqeqeq
  };

  return (
    <div className="default-shadow oph-details-room">
      <div className="oph-details-room-row oph-details-room-header">
        <span className="oph-details-room-col oph-details-room-name"> {t('room.type')} </span>
        <span className="oph-details-room-col">{t('room.count')} </span>
        <span className="oph-details-room-col">
          <span>{t('price_per_person')}</span>
        </span>
        <span className="oph-details-room-col">
          <span>{t('total')}</span>
        </span>
      </div>
      {roomsInfo[0].rooms.map((room, roomIndex) => (
        <div className="oph-details-room-row" key={roomIndex}>
          <span className="oph-details-room-col oph-details-room-name">
            <span>
              <FontAwesomeIcon icon={faBed as any} />
              {room.roomDesc}
            </span>
            <span>
              <FontAwesomeIcon icon={faUtensils as any} /> {room.boardDesc}
            </span>
          </span>
          <span className="oph-details-room-col">
            <SelectCustom
              options={roomsCountSelect}
              value={roomsForCount[roomIndex].count}
              onChange={(event) => setTotalPrice(event, roomIndex)}
              menuPosition="fixed"
              needToTranslate={true}
              disabled={props.soldOut}
            />
          </span>
          <span className="oph-details-room-col oph-details-room-price">
            <div className="text-bold display-flex">
              <span className="oph-details-price-desc">{t('per_person')} /</span>
              <span className="oph-details-room-price-val">
                {formatPriceCurrency(room.pricePerPax, roomsInfo[0].currencySymbol)}
              </span>
            </div>
          </span>
          <span className="oph-details-room-col oph-details-room-price">
            <div className="display-flex">
              <span className="oph-details-price-desc">{t('total')} /</span>
              <span className="oph-details-room-price-val">
                {formatPriceCurrency(room.priceTotal, roomsInfo[0].currencySymbol)}
              </span>
            </div>
          </span>
        </div>
      ))}
      <div className="oph-details-room-footer">
        {areRooms() && (
          <div className="price">
            <span className="title">{t('total')}: </span>
            {formatPriceCurrency(total, roomsInfo[0].currencySymbol)}
          </div>
        )}

        {!props.soldOut && (
          <Button
            className="btn btn-primary btn-lg"
            title="Продолжить"
            onClick={onPressContinue}
            disabled={!areRooms()}
          />
        )}
      </div>
    </div>
  );
};

export default OphirRoomsCard;
